import React, { useState, useContext } from 'react';
import SearchIndexingPreference from './SearchIndexingPreference';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { 
  Download, 
  Trash2, 
  Lock, 
  AlertTriangle, 
  Loader2,
  Shield,
  Database,
  Video
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import VideoCallHistory from './VideoCallHistory';

const AccountSettings = ({ onLogout, showNotification }) => {
  const {user} = useContext(AuthContext);
  const [downloadingData, setDownloadingData] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmPassword, setDeleteConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Download My Data
  const handleDownloadData = async () => {
    try {
      setDownloadingData(true);
      const token = sessionStorage.getItem('token');
      
      const response = await axios.get(`${API}/users/download-my-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Create JSON file and download
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = window.URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `insight-data-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showNotification('success', 'Data exported successfully! Check your downloads folder.');
    } catch (error) {
      showNotification('error', error.response?.data?.detail || 'Failed to export data');
    } finally {
      setDownloadingData(false);
    }
  };

  // Delete My Account
  const handleDeleteAccount = async () => {
    setPasswordError('');
    
    if (!deleteConfirmPassword) {
      setPasswordError('Please enter your password to confirm');
      return;
    }

    try {
      setDeletingAccount(true);
      const token = sessionStorage.getItem('token');

      // First verify password
      try {
        await axios.post(`${API}/auth/login`, {
          email: JSON.parse(sessionStorage.getItem('user')).email,
          password: deleteConfirmPassword
        });
      } catch (error) {
        setPasswordError('Incorrect password');
        setDeletingAccount(false);
        return;
      }

      // Delete account
      await axios.delete(`${API}/users/delete-my-account`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      showNotification('success', 'Account deleted successfully. Your data has been anonymized.');
      
      // Logout after 2 seconds
      setTimeout(() => {
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        onLogout();
      }, 2000);

    } catch (error) {
      showNotification('error', error.response?.data?.detail || 'Failed to delete account');
      setDeletingAccount(false);
    }
  };

  return (
    <div className="space-y-6">
      {user?.role === 'psychologist' && <SearchIndexingPreference />}
      {/* Data Export Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5 text-blue-600" />
            Download My Data
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600">
            Export all your personal data stored in INSIGHT platform. This includes your profile, sessions, 
            consultations, feedback, and analysis data. The data will be downloaded as a JSON file.
          </p>
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
            <p className="text-sm text-gray-700 flex items-start gap-2">
              <Shield className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <span>
                This is your <strong>Right to Access</strong> under GDPR/DPDPA. You can download your data 
                at any time.
              </span>
            </p>
          </div>
          <Button 
            onClick={handleDownloadData} 
            disabled={downloadingData}
            className="w-full sm:w-auto"
          >
            {downloadingData ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Preparing data...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Download My Data
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Account Deletion Section */}
      <Card className="border-red-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="w-5 h-5" />
            Delete My Account
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600">
            Permanently delete your account and anonymize your personal data. This action cannot be undone.
          </p>
          
          <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded space-y-2">
            <p className="text-sm text-red-800 font-semibold">
              ⚠️ Warning: This will:
            </p>
            <ul className="text-sm text-red-700 space-y-1 ml-4">
              <li>• Anonymize your name and email</li>
              <li>• Delete all your analysis data permanently</li>
              <li>• Remove your profile information</li>
              <li>• Keep anonymized data for analytics only</li>
              <li>• Log you out immediately</li>
            </ul>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
            <p className="text-sm text-gray-700 flex items-start gap-2">
              <Shield className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <span>
                This is your <strong>Right to Erasure</strong> under GDPR/DPDPA. We will anonymize your 
                data while retaining anonymized records for legal and analytics purposes.
              </span>
            </p>
          </div>

          <Button 
            onClick={() => setShowDeleteDialog(true)} 
            variant="destructive"
            className="w-full sm:w-auto"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete My Account
          </Button>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-6 h-6" />
              Delete Account - Are You Sure?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p className="text-gray-700 font-semibold">
                This action is PERMANENT and CANNOT be undone.
              </p>
              
              <div className="bg-red-50 p-3 rounded border border-red-200">
                <p className="text-sm text-red-800 font-semibold mb-2">You will lose:</p>
                <ul className="text-sm text-red-700 space-y-1">
                  <li>• All your session and analysis data</li>
                  <li>• All your consultations and appointments</li>
                  <li>• All your messages and feedback</li>
                  <li>• Access to your account permanently</li>
                </ul>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password" className="text-gray-700 font-semibold">
                  Enter your password to confirm:
                </Label>
                <Input
                  id="confirm-password"
                  type="password"
                  placeholder="Enter your password"
                  value={deleteConfirmPassword}
                  onChange={(e) => {
                    setDeleteConfirmPassword(e.target.value);
                    setPasswordError('');
                  }}
                  className={passwordError ? 'border-red-500' : ''}
                />
                {passwordError && (
                  <p className="text-sm text-red-600">{passwordError}</p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              onClick={() => {
                setDeleteConfirmPassword('');
                setPasswordError('');
              }}
              disabled={deletingAccount}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAccount}
              disabled={deletingAccount || !deleteConfirmPassword}
              className="bg-red-600 hover:bg-red-700"
            >
              {deletingAccount ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Yes, Delete My Account
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Video Call History */}
      <VideoCallHistory 
        userRole={JSON.parse(sessionStorage.getItem('user') || '{}').role}
        showNotification={showNotification}
      />
    </div>
  );
};

export default AccountSettings;
